import FormField from 'lib/src/components/form/FormField';
import usePrevious from 'lib/src/hooks/usePrevious';
import { DropdownOption } from 'lib/src/types/shared/DropdownOption';
import { FormInputProps } from 'lib/src/types/shared/FormInputProps';
import { useState, useRef, useEffect, useMemo, SyntheticEvent } from 'react';
import { isGA } from 'lib/src/constants/environment';
import useFieldValidation from './hooks/useFieldValidation';

// Select is a single select dropdown
// pass 'required' flag if it's required
// pass 'search' flag if you want to enable the search
// pass 'disabled' flag if you want to disable the dropdown
// pass a 'placeholder' string if you want to customize the placeholder
// pass a 'label' string if you want to output a field name above the select.
// field errors will be output below automatically
// options should be in this form '[{ value: 1, label: "opt 1" }, { value: 2, label: "opt 2" }]'
// value should be the selected 'value' not option(the number not the object)
// pass `null` as the default value
const Select: React.FC<SelectProps> = ({
    name,
    search = false,
    disabled = false,
    value = null,
    options = [],
    onChange,
    label = '',
    placeholder = 'Select...',
    required,
    canBeEmpty = true,
    customValidate,
    fieldClassName,
    forceAsterisk = false,
    scrollRef = null,
}) => {
    const [error, showError] = useFieldValidation({
        name,
        required,
        customValidate,
        value,
    });

    const [searchTerm, setSearch] = useState('');
    const node = useRef<HTMLDivElement | null>(null);

    const [isOpen, setIsOpen] = useState(false);
    const prevIsOpen = usePrevious(isOpen);

    useEffect(() => {
        if (!isOpen && prevIsOpen) {
            showError();
        }
    }, [isOpen, showError, prevIsOpen]);

    useEffect(() => {
        document.addEventListener('mousedown', handleClick);

        return () => {
            document.removeEventListener('mousedown', handleClick);
        };
    }, []);

    const filteredOptions = useMemo(_getFilteredOptions, [search, searchTerm, options]);
    const selected = useMemo(_getSelected, [value, options]);

    let upwards = false;
    try {
        if (node.current && scrollRef?.current) {
            upwards =
                node.current.getBoundingClientRect().bottom >
                scrollRef.current.getBoundingClientRect().bottom - 180; // If field is within 180px of the bottom of the form scroll, add upwards class
        }
    } catch (e) {
        console.warn('Form select upwards ref failure:', e);
    }

    return (
        <FormField
            name={name}
            label={label}
            required={required}
            error={error}
            className={fieldClassName}
            forceAsterisk={forceAsterisk}
        >
            <div ref={node} onClick={() => !disabled && setIsOpen(!isOpen)}>
                <div
                    className={`form-select filter-button ${disabled ? 'disabled' : ''} ${
                        error ? 'error' : ''
                    } ${isOpen ? 'open' : ''}`}
                >
                    {!selected ? (
                        <p className="placeholder">{placeholder}</p>
                    ) : (
                        <>
                            <p>{selected.label}</p>
                            {!disabled && canBeEmpty && (
                                <button
                                    className={`remove ${isGA ? 'ga' : 'ix'}`}
                                    onClick={e => {
                                        e.preventDefault();
                                        e.stopPropagation();

                                        handleChange(null);
                                    }}
                                >
                                    <i className="fal fa-times" />
                                </button>
                            )}
                        </>
                    )}
                    <i className={`filter-arrow fal fa-chevron-down ${isGA ? 'ga' : 'ix'}`} />
                </div>

                {isOpen && (
                    <div className={`form-select-options ${upwards && 'upwards'}`}>
                        {search && !!options.length && (
                            <div className="search" onClick={e => e.stopPropagation()}>
                                <input
                                    className="form-input"
                                    type="text"
                                    placeholder="Search..."
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                />
                            </div>
                        )}
                        <div className="options-list">
                            {!filteredOptions.length && <p>There are no options to display</p>}

                            {filteredOptions.map((opt, i) => (
                                <p
                                    key={`${opt.value} - ${i}`}
                                    className={`option ${value === opt.value ? 'active' : ''}`}
                                    onClick={e => {
                                        e.preventDefault();
                                        handleChange(opt.value);
                                    }}
                                >
                                    {opt.label}
                                </p>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </FormField>
    );

    function _getSelected() {
        return options.find(item => item.value === value);
    }

    function _getFilteredOptions() {
        if (!search || !searchTerm) return options;
        return options.filter(opt =>
            opt.label
                .replace(/[^A-Z0-9]/gi, '')
                .toLowerCase()
                .includes(searchTerm.replace(/[^A-Z0-9]/gi, '').toLowerCase()),
        );
    }

    function handleClick(e: Event) {
        // inside click
        if (node?.current?.contains(e.target as Node)) {
            return;
        }

        // outside click
        setIsOpen(false);
    }

    function handleSearchChange(e: SyntheticEvent) {
        e.preventDefault();

        setSearch((e.target as HTMLTextAreaElement).value);
    }

    function handleChange(val: number | null) {
        if (value === val) return;
        onChange(name, val);
    }
};

interface SelectProps extends FormInputProps<number | null> {
    search?: boolean;
    options?: DropdownOption<number>[];
    label?: string;
    placeholder?: string;
    fieldClassName?: string;
    canBeEmpty?: boolean;
    forceAsterisk?: boolean;
    scrollRef?: React.RefObject<HTMLDivElement>;
}

export default Select;
